var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { Axis } from '../types';
import { toRadians } from '../util/math-util';
import { log, FACILITY, format } from '../util/log';
import { EventEmitter } from '../util/event-emitter';
import { Quaternion } from './quaternion';
var LOG = FACILITY.ALIGNMENT;
export var Type;
(function (Type) {
    Type[Type["None"] = 0] = "None";
    Type[Type["Version_DEV"] = 1] = "Version_DEV";
    Type[Type["Version_1"] = 2] = "Version_1";
})(Type || (Type = {}));
export var AlignmentEvent;
(function (AlignmentEvent) {
    AlignmentEvent["Update"] = "Update";
})(AlignmentEvent || (AlignmentEvent = {}));
export var State;
(function (State) {
    State[State["None"] = 0] = "None";
    State[State["Begin"] = 1] = "Begin";
    State[State["Wait_Still"] = 2] = "Wait_Still";
    State[State["Sensor_Reset"] = 3] = "Sensor_Reset";
    State[State["Wait_Reset_Begin"] = 4] = "Wait_Reset_Begin";
    State[State["Wait_Reset_Complete"] = 5] = "Wait_Reset_Complete";
    State[State["Wait_TPose"] = 6] = "Wait_TPose";
    State[State["Wait_Stand_After_TPose"] = 7] = "Wait_Stand_After_TPose";
    State[State["Wait_LeanBack"] = 8] = "Wait_LeanBack";
    State[State["Wait_Stand_After_LeanBack"] = 9] = "Wait_Stand_After_LeanBack";
    State[State["Wait_March"] = 10] = "Wait_March";
    State[State["Complete"] = 11] = "Complete";
    State[State["Error"] = 12] = "Error";
})(State || (State = {}));
var AlignmentConfig = (function () {
    function AlignmentConfig() {
        this.frames = [];
        this.axisTransforms = [];
        this.qInitials = [];
    }
    AlignmentConfig.prototype.init = function () {
        this.frames = [];
        this.axisTransforms = null;
        this.qInitials = null;
    };
    AlignmentConfig.prototype.isValid = function () {
        var valid = (10 === this.axisTransforms.length && 10 === this.qInitials.length);
        log.debug(LOG, "AlignmentConfig valid:" + valid);
        return valid;
    };
    AlignmentConfig.prototype.copyTo = function (dest) {
        dest.frames = this.frames.map(function (el) { return el; });
        dest.axisTransforms = this.axisTransforms.map(function (el) { return el; });
        dest.qInitials = this.qInitials.map(function (el) { return el; });
    };
    AlignmentConfig.prototype.copyFrom = function (src) {
        this.frames = src.frames.map(function (el) { return el; });
        this.axisTransforms = src.axisTransforms.map(function (el) { return el; });
        this.qInitials = src.qInitials.map(function (el) { return el; });
    };
    return AlignmentConfig;
}());
export { AlignmentConfig };
var Alignment = (function () {
    function Alignment(body) {
        this.state = State.None;
        this.type = Type.None;
        this.body = body;
        this.context = undefined;
        this.config = new AlignmentConfig();
    }
    Alignment.prototype.save = function (dest) {
        this.config.copyTo(dest);
    };
    Alignment.prototype.load = function (src) {
        var _this = this;
        log.info(LOG, "Alignment.load\n" + JSON.stringify(src, null, 2));
        this.config.copyFrom(src);
        this.body.partMap.forEach(function (part, index) {
            var qInitial = _this.config.qInitials.length > index ? _this.config.qInitials[index] : undefined;
            var axisTransform = _this.config.axisTransforms.length > index ? _this.config.axisTransforms[index] : undefined;
            _this.body.kchain[part].setAlignment(qInitial, axisTransform);
        });
        this.state = State.Complete;
    };
    Alignment.prototype.begin = function (type) {
        if (type === void 0) { type = Type.Version_1; }
        this.type = type;
        this.state = State.Begin;
    };
    Alignment.prototype.beginSensorReset = function () {
        log.debug(LOG, 'beginSensorReset() called');
        this.setState(State.Wait_Reset_Begin);
    };
    Alignment.prototype.abort = function () {
        this.type = Type.None;
        this.state = State.None;
        this.body.clearAxisTransforms();
    };
    Alignment.prototype.getState = function () {
        return this.state;
    };
    Alignment.prototype.getInProcess = function () {
        return (this.state >= State.Begin && this.state < State.Complete);
    };
    Alignment.prototype.setState = function (state) {
        log.debug(LOG, State[this.state] + " => " + State[state]);
        this.state = state;
        this.context = {};
    };
    Alignment.prototype.handleTimeouts = function () {
        var _this = this;
        if (!this.context.startTime) {
            this.context.startTime = Date.now();
        }
        var timeoutMs = 10000;
        var elapsed = Date.now() - this.context.startTime;
        if (elapsed > timeoutMs) {
            var errorInfo = '';
            switch (this.state) {
                case State.Wait_Still:
                case State.Wait_Reset_Complete:
                case State.Wait_Stand_After_TPose:
                case State.Wait_Stand_After_LeanBack:
                    errorInfo = State[this.state] + "," + this.context.failure;
                    break;
                case State.Sensor_Reset:
                    errorInfo = "" + State[this.state];
                    break;
                case State.Wait_TPose:
                    errorInfo = State[this.state] + "," +
                        Alignment.segments.arms
                            .filter(function (part) { return _this.body.kchain[part].aligned === false; })
                            .join();
                    break;
                case State.Wait_LeanBack:
                    errorInfo = State[this.state] + "," +
                        Alignment.segments.body
                            .filter(function (part) { return _this.body.kchain[part].aligned === false; })
                            .join();
                    break;
                case State.Wait_March:
                    errorInfo = State[this.state] + "," +
                        Alignment.segments.legs
                            .filter(function (part) { return _this.body.kchain[part].aligned === false; })
                            .join();
                    break;
                case State.None:
                case State.Begin:
                case State.Wait_Reset_Begin:
                case State.Complete:
                default:
                    return;
            }
            log.error(LOG, "Alignment Error: " + errorInfo);
            this.setState(State.Error);
            EventEmitter.emit(AlignmentEvent.Update, this.createMsg(errorInfo));
            this.abort();
        }
    };
    Alignment.prototype.createMsg = function (info) {
        if (info === void 0) { info = ''; }
        return {
            state: this.state,
            info: info,
        };
    };
    Alignment.prototype.update = function () {
        switch (this.state) {
            case State.Begin:
                this.setState(State.Wait_Still);
                EventEmitter.emit(AlignmentEvent.Update, this.createMsg());
                this.config.init();
                break;
            case State.Wait_Still:
                if (this.isStill(this.body.partMap, Alignment.waitSensorReset)) {
                    this.setState(State.Sensor_Reset);
                    EventEmitter.emit(AlignmentEvent.Update, this.createMsg());
                    delete this.context.t1;
                }
                else {
                    this.handleTimeouts();
                }
                break;
            case State.Sensor_Reset:
                this.handleTimeouts();
                break;
            case State.Wait_Reset_Begin: {
                if (!this.context.resetBegin) {
                    this.context.resetBegin = Date.now();
                    log.debug(LOG, 'Wait_Reset_Begin start timer', this.context);
                }
                var elapsed = Date.now() - this.context.resetBegin;
                if (elapsed > Alignment.waitSensorReset) {
                    this.setState(State.Wait_Reset_Complete);
                }
                break;
            }
            case State.Wait_Reset_Complete:
                if (this.receivingData()) {
                    if (this.isStill(this.body.partMap, Alignment.waitStand)) {
                        this.body.setAlignment(this.body.getQSensors());
                        this.setState(State.Wait_TPose);
                        EventEmitter.emit(AlignmentEvent.Update, this.createMsg());
                        delete this.context.t1;
                        return;
                    }
                }
                this.handleTimeouts();
                break;
            case State.Wait_TPose:
                if (this.poseComplete(Alignment.segments.arms)) {
                    this.setState(State.Wait_Stand_After_TPose);
                    EventEmitter.emit(AlignmentEvent.Update, this.createMsg());
                }
                else {
                    this.handleTimeouts();
                }
                break;
            case State.Wait_Stand_After_TPose:
                if (this.isStill(Alignment.segments.body, Alignment.waitStand)) {
                    if (this.isStanding(Alignment.segments.body)) {
                        this.setState(State.Wait_LeanBack);
                        EventEmitter.emit(AlignmentEvent.Update, this.createMsg());
                        return;
                    }
                    delete this.context.t1;
                }
                this.handleTimeouts();
                break;
            case State.Wait_LeanBack:
                if (this.poseComplete(Alignment.segments.body)) {
                    this.setState(State.Wait_Stand_After_LeanBack);
                    EventEmitter.emit(AlignmentEvent.Update, this.createMsg());
                }
                else {
                    this.handleTimeouts();
                }
                break;
            case State.Wait_Stand_After_LeanBack:
                if (this.isStill(Alignment.segments.legs, Alignment.waitStand)) {
                    if (this.isStanding(Alignment.segments.legs)) {
                        this.setState(State.Wait_March);
                        EventEmitter.emit(AlignmentEvent.Update, this.createMsg());
                        return;
                    }
                    delete this.context.t1;
                }
                this.handleTimeouts();
                break;
            case State.Wait_March:
                if (this.poseComplete(Alignment.segments.legs)) {
                    this.setState(State.Complete);
                    EventEmitter.emit(AlignmentEvent.Update, this.createMsg());
                    this.config.axisTransforms = this.body.getAxisTransforms();
                    this.config.qInitials = this.body.getQInitials();
                }
                else {
                    this.handleTimeouts();
                }
                break;
            default:
                log.error(LOG, 'Invalid alignment state ', this.state);
                break;
        }
    };
    Alignment.prototype.push = function (frame) {
        if (!frame) {
            return;
        }
        this.config.frames.push(__assign(__assign({}, frame), { state: this.state }));
    };
    Alignment.prototype.getPoseT1 = function () {
        var _this = this;
        return {
            qSensors: this.body.partMap.reduce(function (acc, part) {
                acc[part] = _this.body.kchain[part].qSensor;
                return acc;
            }, {}),
            timestamp: this.body.kchain.hip.timestamp,
        };
    };
    Alignment.prototype.isStill = function (parts, durationMs) {
        var _this = this;
        if (!this.context.t1) {
            this.context.t1 = this.getPoseT1();
            log.debug(LOG, 'isStill() start timer');
            return false;
        }
        var partMoved = function (part) {
            if (_this.body.kchain[part].aligned) {
                return false;
            }
            var delta = _this.body.kchain[part].qSensor.delta(_this.context.t1.qSensors[part]);
            var partIsMoving = delta.w <= 0.98;
            return partIsMoving;
        };
        var movingParts = parts.filter(partMoved);
        if (movingParts.length === 0) {
            if (this.body.kchain.hip.timestamp - this.context.t1.timestamp >= durationMs) {
                return true;
            }
        }
        else {
            delete this.context.t1;
            this.context.failure = "failed still: " + movingParts.join();
        }
        return false;
    };
    Alignment.prototype.isStanding = function (parts) {
        var _this = this;
        if (this.type === Type.Version_DEV) {
            return true;
        }
        var partsNotStanding = parts.filter(function (part) {
            if (_this.body.kchain[part].aligned) {
                return false;
            }
            return _this.body.kchain[part].qModel.w < 0.985;
        });
        if (partsNotStanding.length > 0) {
            this.context.failure = "failed standing: " + partsNotStanding.join();
            return false;
        }
        return true;
    };
    Alignment.prototype.receivingData = function () {
        if (!this.context.lastHipTimestamp) {
            this.context.lastHipTimestamp = this.body.kchain.hip.timestamp;
            return false;
        }
        if (this.context.lastHipTimestamp === this.body.kchain.hip.timestamp) {
            this.context.failure = 'Not receiving data';
            return false;
        }
        return true;
    };
    Alignment.prototype.getAlignmentAxis = function (part) {
        if (this.type === Type.Version_DEV) {
            return [0, 1, 0];
        }
        if (part.includes('arm')) {
            if (part.includes('right')) {
                return [-1, 0, 0];
            }
            return [1, 0, 0];
        }
        if (part.includes('leg')) {
            if (part.includes('upper')) {
                return [0, -1, 0];
            }
            return [0, 1, 0];
        }
        if (part.includes('hip') || part.includes('torso')) {
            return [0, -1, 0];
        }
        throw Error('getAlignmentAxis: Unknown part');
    };
    Alignment.prototype.getAlignmentAngle = function () {
        switch (this.state) {
            case State.Wait_TPose:
                return toRadians(40);
            case State.Wait_LeanBack:
                return toRadians(11);
            case State.Wait_March:
                return toRadians(16);
            default:
                throw Error('getAlignmentAngle: Invalid state');
        }
    };
    Alignment.prototype.poseComplete = function (segments) {
        var results = segments.map(this.checkPose, this);
        return !results.includes(false);
    };
    Alignment.prototype.checkPose = function (part) {
        if (this.body.kchain[part].aligned === true) {
            return true;
        }
        var axis = this.getAlignmentAxis(part);
        var angle = this.getAlignmentAngle();
        var swingAoR = this.body.kchain[part].qModel.axisAngle();
        var rotationIndex = axis.findIndex(function (x) { return Math.abs(x) === 1; });
        if (rotationIndex === -1) {
            log.error(LOG, "Invalid alignment axis " + format(axis));
            return false;
        }
        if (swingAoR.theta > angle) {
            var expectedDirection = Math.sign(axis[rotationIndex]);
            var direction = Math.sign(swingAoR.axis[rotationIndex]);
            var magnitude = Math.abs(swingAoR.axis[rotationIndex]);
            var threshold = 0.5;
            if (direction === expectedDirection && magnitude > threshold) {
                swingAoR.axis[Axis.Z] = 0;
                var qAxisTransform = Quaternion.fromBetweenVectors(swingAoR.axis, axis);
                this.body.kchain[part].setAxisTransform(qAxisTransform);
                log.debug(LOG, "" + this.body.kchain[part].name);
                log.debug(LOG, "  qModel           : " + format(this.body.kchain[part].qModel));
                log.debug(LOG, "  axis measured    : " + format(swingAoR.axis) + " theta:" + format(swingAoR.theta));
                log.debug(LOG, "  axis target      : " + axis);
                log.debug(LOG, "  transform        : " + format(qAxisTransform));
                return true;
            }
        }
        return false;
    };
    Alignment.waitStand = 750;
    Alignment.waitSensorReset = 1500;
    Alignment.segments = {
        body: ['hip', 'torso'],
        arms: ['right-upper-arm', 'right-lower-arm', 'left-upper-arm', 'left-lower-arm'],
        legs: ['right-upper-leg', 'right-lower-leg', 'left-upper-leg', 'left-lower-leg'],
    };
    return Alignment;
}());
export { Alignment };
//# sourceMappingURL=alignment.js.map